import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Member + staff forgot-password page. Backend always returns the same
// generic 200 response regardless of whether the email exists — we mirror
// that here so the UI never reveals account state.
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch {
      // Backend never errors on this endpoint, but if the network fails we
      // still show the same generic success — security over UX clarity here.
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#002249] via-[#1D3557] to-[#002249] flex items-center justify-center px-4"
      data-testid="forgot-password-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#002249] to-[#1D3557] px-8 py-7 text-center">
            <ShieldCheck className="w-11 h-11 text-white mx-auto mb-2.5" />
            <h1 className="text-xl font-bold text-white">Reset your password</h1>
            <p className="text-white/60 text-xs mt-1">Dominican Chamber of Commerce of Florida</p>
          </div>

          {submitted ? (
            <div className="p-8 text-center" data-testid="forgot-password-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-[#002249] mb-2">Check your inbox</h2>
              <p className="text-slate-600 text-sm leading-relaxed">
                If an account exists with this email, a reset link has been sent.
                The link expires in <strong>60 minutes</strong>.
              </p>
              <p className="text-slate-400 text-xs mt-4">
                Didn't receive an email? Check your spam folder, or contact{' '}
                <a href="mailto:info@dominicanchamberfl.org" className="text-[#002249] underline">
                  info@dominicanchamberfl.org
                </a>.
              </p>
              <Link
                to="/member-login"
                className="inline-flex items-center gap-1.5 text-sm text-[#002249] font-semibold mt-6 hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-5" data-testid="forgot-password-form">
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                Enter the email address associated with your DCCF account. We'll send you a secure link to reset your password.
              </p>

              <div>
                <Label htmlFor="fp-email" className="text-xs font-semibold text-slate-700">Email address</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="fp-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 h-11 rounded-lg"
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#E63946] hover:bg-[#d32f3f] rounded-lg font-semibold text-sm"
                data-testid="forgot-password-submit"
              >
                {loading ? 'Sending...' : (
                  <>
                    Send reset link
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>

              <div className="text-center pt-2">
                <Link
                  to="/member-login"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#002249]"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
